/**
 * Bottom HUD while navigating: ETA / remaining, current speed, recenter and
 * end buttons, plus transient status toasts (rerouting, offline).
 */

import type { NavProgress } from '../core/types';
import { formatDistance, formatEta, formatSpeed, speedUnitLabel } from '../core/format';
import type { UnitSystem } from '../core/settings';
import { t } from '../core/i18n';

/**
 * Remaining time sized for a stat column: "44 min" under an hour, "1:44"
 * above it. The long form ("1 hr 44 min") was wider than its third of the bar
 * and clipped to "hr 44 mi" on a phone - a number that read as nonsense.
 */
function compactDuration(seconds: number): string {
  const m = Math.max(0, Math.round(seconds / 60));
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

/**
 * Write only when the value actually changed.
 *
 * Assigning textContent replaces the text node whether or not the string
 * differs, and every one of these sits inside `.hud__bar`, which carries a
 * `backdrop-filter: blur(18px)`. Dirtying a node under a backdrop filter forces
 * the blurred region - sampled from the moving map behind it - to be composited
 * again. Once a second, on a phone with a weak GPU, that is what the driver
 * sees as the text shimmering. Measured on this HUD: half of all writes were
 * setting a string that was already there, and the unit label ("km/h") was
 * rewritten on every single fix having never once changed.
 */
function setText(el: HTMLElement | null, value: string): void {
  if (el && el.textContent !== value) el.textContent = value;
}

export class NavHud {
  readonly el: HTMLElement;
  private recenterEl: HTMLButtonElement;
  private toastTimer: number | null = null;
  /** Looked up once; these nodes live for the lifetime of the HUD. */
  private etaEl!: HTMLElement;
  private distEl!: HTMLElement;
  private durEl!: HTMLElement;
  private speedEl!: HTMLElement;
  private unitEl!: HTMLElement;

  constructor(
    parent: HTMLElement,
    private actions: { onEnd: () => void; onRecenter: () => void; onOverview: () => void },
  ) {
    this.el = document.createElement('div');
    this.el.className = 'hud';
    this.el.hidden = true;
    this.el.innerHTML = `
      <div class="hud__toast" hidden></div>
      <div class="hud__bar">
        <button class="hud__end" aria-label="End navigation">
          <svg viewBox="0 0 24 24"><path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>
        </button>
        <div class="hud__stats" role="button" tabindex="0" aria-label="Route overview">
          <div class="hud__stat hud__stat--eta">
            <span class="hud__eta">--:--</span>
            <span class="hud__stat-label">${t('hud.arrive')}</span>
          </div>
          <div class="hud__stat">
            <span class="hud__dist">--</span>
            <span class="hud__stat-label">${t('hud.left')}</span>
          </div>
          <div class="hud__stat">
            <span class="hud__dur">--</span>
            <span class="hud__stat-label">${t('hud.time')}</span>
          </div>
        </div>
        <div class="hud__speed">
          <div class="hud__speed-value">--</div>
          <div class="hud__speed-unit">km/h</div>
        </div>
      </div>`;
    parent.appendChild(this.el);

    this.etaEl = this.el.querySelector('.hud__eta')!;
    this.distEl = this.el.querySelector('.hud__dist')!;
    this.durEl = this.el.querySelector('.hud__dur')!;
    this.speedEl = this.el.querySelector('.hud__speed-value')!;
    this.unitEl = this.el.querySelector('.hud__speed-unit')!;

    /*
     * Re-centre lives OUTSIDE the HUD stack.
     *
     * Inside it, it sat directly under the Spotify strip - which docks above
     * the HUD during navigation and is painted over it. A round GPS button
     * beside the strip is both visible and balances the bottom of the screen,
     * which is otherwise all weight on the left.
     */
    this.recenterEl = document.createElement('button');
    this.recenterEl.className = 'hud__recenter';
    this.recenterEl.hidden = true;
    this.recenterEl.setAttribute('aria-label', t('nav.recenter'));
    this.recenterEl.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="6.2" fill="none" stroke="currentColor" stroke-width="2"/>
        <circle cx="12" cy="12" r="2.1" fill="currentColor"/>
        <path d="M12 1.6 V5 M12 19 v3.4 M1.6 12 H5 M19 12 h3.4" stroke="currentColor"
              stroke-width="2" stroke-linecap="round"/>
      </svg>`;
    parent.appendChild(this.recenterEl);
    this.recenterEl.addEventListener('click', () => this.actions.onRecenter());

    this.el.querySelector('.hud__end')!.addEventListener('click', () => this.actions.onEnd());
    this.el.querySelector('.hud__stats')!.addEventListener('click', () => this.actions.onOverview());
  }

  show(): void {
    this.el.hidden = false;
  }

  hide(): void {
    this.el.hidden = true;
    this.setRecenterVisible(false);
  }

  update(progress: NavProgress, speedMs: number | null, units: UnitSystem): void {
    setText(this.etaEl, formatEta(progress.durationRemaining));
    setText(this.distEl, formatDistance(progress.distanceRemaining, units));
    setText(this.durEl, compactDuration(progress.durationRemaining));
    this.updateSpeed(speedMs, units);
  }

  /** Keep the instrument current even before a route begins. */
  updateSpeed(speedMs: number | null, units: UnitSystem): void {
    // Once GPS is active, a missing platform speed means "not moving yet",
    // not "hide the instrument". The watcher derives speed on later fixes.
    setText(this.speedEl, formatSpeed(speedMs ?? 0, units));
    setText(this.unitEl, speedUnitLabel(units));
  }

  setRecenterVisible(visible: boolean): void {
    this.recenterEl.hidden = !visible;
  }

  /**
   * Transient status line. `action` adds a single tappable button - used by the
   * fuel detour so a mis-tap is one tap to reverse rather than a re-route.
   */
  toast(
    message: string,
    sticky = false,
    action?: { label: string; onAction: () => void },
  ): void {
    const el = this.el.querySelector('.hud__toast') as HTMLElement;
    el.textContent = message;
    el.hidden = false;

    if (action) {
      const btn = document.createElement('button');
      btn.className = 'hud__toast-action';
      btn.textContent = action.label;
      btn.addEventListener('click', () => {
        action.onAction();
        el.hidden = true;
      });
      el.appendChild(btn);
    }

    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
    if (!sticky) {
      this.toastTimer = window.setTimeout(() => (el.hidden = true), action ? 10_000 : 4000);
    }
  }

  clearToast(): void {
    (this.el.querySelector('.hud__toast') as HTMLElement).hidden = true;
    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
  }
}
